import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Route, Switch, Redirect, BrowserRouter as Router } from "react-router-dom";
import { Document, usePDF } from '@react-pdf/renderer';
import LoadingIndeterminate from './emit-components/LoadingIndeterminate';
import AboutView from './emit-components/AboutView';
import { calculateEmissionsFromVehicleData, preprocessVehicleList, processGraphData, summarizeEmissions } from './emit-components/EmissionsHelpers';
import ProductHeader from './emit-components/ProductHeader';
import "../component-css/emit-stylesheets/swt-emit.css";
import PdfReport from './emit-components/PdfReport';
import { hasAltFuelVehicles, deriveMethodObjFromSettings, pageHasData, noDataMessageText, filterForSelectedClasses, convertEmissionData, convertSummaryData, convertGraphData } from './emit-components/UtilityFunctions';
import NavBar from './emit-components/NavBar';
import { ContentContainer, ProductWrapper } from '../styles/emit-styles/Emit-styles';
import * as S from '../styles/emit-styles/Emit-styles';
import HomeView from './emit-components/HomeView';
import GeneratedEmissionsView from './emit-components/GeneratedEmissionsView';
import AvoidedEmissionsView from './emit-components/AvoidedEmissionsView';
import { fetchDateBounds, fetchDbSettings, fetchGraphData, fetchGroups, fetchVehicleClasses, fetchVehicleData, formatDateParamsForApi, sawatchApiFetchCall } from './emit-components/FetchHelpers';

export default function Emit({ user, apiURL, dbName, basename, dbDisplayName, secrets }) {

    // *** STATE MANAGEMENT ***
    // --- Settings ---
    const [dbSettings, setDbSettings] = useState([]);
    // Aggregating user measurement system preferences into a set of booleans for prop drilling to display helper functions
    const metricUnitPrefs = useMemo(() => ({
        emissions: user.userSettings.use_metric_emissions,
        distance: user.userSettings.use_kms,
        volume: user.userSettings.use_liters
    }), [user.userSettings]);

    // --- Dates ---
    // User selected range
    const [beginDate, setBeginDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    // Overall fleet range
    const [minDate, setMinDate] = useState(null);
    const [maxDate, setMaxDate] = useState(null);

    // --- Groups ---
    const [groups, setGroups] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState(null);

    // --- Classes ---
    const [vehicleClasses, setVehicleClasses] = useState([]);
    const [selectedVehicleClasses, setSelectedVehicleClasses] = useState([]);

    // --- App Data ---
    const [rawEmissionData, setRawEmissionData] = useState(null);
    const [classFilteredEmissionData, setClassFilteredEmissionData] = useState(null);
    const [convertedEmissionData, setConvertedEmissionData] = useState(null); // Used in generated emissions table

    const [rawSummaryData, setRawSummaryData] = useState(null);
    const [convertedSummaryData, setConvertedSummaryData] = useState(null); // Used in info cards & distribution cards (also informs graph values)

    const [rawGraphData, setRawGraphData] = useState(null);
    const [convertedGraphData, setConvertedGraphData] = useState(null); // Used in graphs

    const [fleetHasAltFuels, setFleetHasAltFuels] = useState(null) // Used in no data messaging

    // --- Processing Internals ---
    const [methodology, setMethodology] = useState(null); // 2 options: { value: 'ghgprotocol_2.0.0', label: 'GHG Protocol 2.0.0'} & { value: 'moves3', label: 'MOVES3' }
    const isTd = dbSettings.is_td; // Expliclity declared to make prop drilling easier
    const [calcByFuel, setCalcByFuel] = useState(true); // By mile calculation is supported by code and API, but not available through this interface - MH

    // --- Display Preferences ---
    const [macroScale, setMacroScale] = useState(true);
    const [showEmissionsByDrivetrain, setShowEmissionsByDrivetrain] = useState(false); // Stubbed out here and in controls. NOT IMPLEMENTED. Further considerations needed around this functionality before it is fully realized. -NK
    // Logic is sound but we are turning off weekly data display for now - JJ 3/3/25
    const showMonths = true; // (graphData && graphData?.monthlyStartDates?.length >= USE_MONTHS_BREAKPOINT);

    const [convertedDataLoading, setConvertedDataLoading] = useState(true);

    // --- Loading and Error States for Data Fetches ---
    const [isLoadingBounds, setIsLoadingBounds] = useState(true);
    const [dateBoundsError, setDateBoundsError] = useState(null);
    const [isLoadingVehicleClasses, setIsLoadingVehicleClasses] = useState(true);
    const [vehicleClassesError, setVehicleClassesError] = useState(null);
    const [isLoadingGroups, setIsLoadingGroups] = useState(true);
    const [groupsError, setGroupsError] = useState(null);
    const [isLoadingDbSettings, setIsLoadingDbSettings] = useState(true);
    const [dbSettingsError, setDbSettingsError] = useState(null);
    const [isLoadingEntireFleet, setIsLoadingEntireFleet] = useState(true);
    const [entireFleetDataError, setEntireFleetDataError] = useState(null);

    const [isLoadingVehicleData, setIsLoadingVehicleData] = useState(false);
    const [vehicleDataError, setVehicleDataError] = useState(null);
    const [isLoadingIdleData, setIsLoadingIdleData] = useState(false);
    const [idleDataError, setIdleDataError] = useState(null);
    const [isLoadingGraphData, setIsLoadingGraphData] = useState(false);
    const [graphDataError, setGraphDataError] = useState(null);


    // --- Data Storage for Fetched Data ---
    const [fetchedBoundsData, setFetchedBoundsData] = useState(null);
    const [fetchedVehicleClassesData, setFetchedVehicleClassesData] = useState(null);
    const [fetchedGroupsData, setFetchedGroupsData] = useState(null);
    const [fetchedDbSettingsData, setFetchedDbSettingsData] = useState(null);
    const [fetchedEntireFleetVehicleData, setFetchedEntireFleetVehicleData] = useState(null);
    const [fetchedVehicleData, setFetchedVehicleData] = useState(null);
    const [fetchedIdleData, setFetchedIdleData] = useState(null);
    const [fetchedGraphData, setFetchedGraphData] = useState(null);


    // *** FETCHING & PROCESSING ***

    // Immutable data fetches
    useEffect(() => {
        const fetchData = async () => {
            setIsLoadingBounds(true);
            setDateBoundsError(null);
            try {
                const data = await fetchDateBounds(user)(`${apiURL}getBounds?dbName=${dbName}`);
                setFetchedBoundsData(data);
            } catch (err) {
                setDateBoundsError(err);
            } finally {
                setIsLoadingBounds(false);
            }
        };
        fetchData();
    }, [apiURL, dbName, user]);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoadingVehicleClasses(true);
            setVehicleClassesError(null);
            try {
                const data = await fetchVehicleClasses(user)(`${apiURL}getVehicleClassesPresent?dbName=${dbName}`);
                setFetchedVehicleClassesData(data);
            } catch (err) {
                setVehicleClassesError(err);
            } finally {
                setIsLoadingVehicleClasses(false);
            }
        };
        fetchData();
    }, [apiURL, dbName, user]);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoadingGroups(true);
            setGroupsError(null);
            try {
                const data = await fetchGroups(user)(`${apiURL}getGroups?dbName=${dbName}`);
                setFetchedGroupsData(data);
            } catch (err) {
                setGroupsError(err);
            } finally {
                setIsLoadingGroups(false);
            }
        };
        fetchData();
    }, [apiURL, dbName, user]);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoadingDbSettings(true);
            setDbSettingsError(null);
            try {
                const data = await fetchDbSettings(user)(`${apiURL}getSettings?dbName=${dbName}`);
                setFetchedDbSettingsData(data);
            } catch (err) {
                setDbSettingsError(err);
            } finally {
                setIsLoadingDbSettings(false);
            }
        };
        fetchData();
    }, [apiURL, dbName, user]);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoadingEntireFleet(true);
            setEntireFleetDataError(null);
            try {
                const data = await sawatchApiFetchCall(user)(`${apiURL}emitDataForFleet?dbName=${dbName}`);
                setFetchedEntireFleetVehicleData(data);
            } catch (err) {
                setEntireFleetDataError(err);
            } finally {
                setIsLoadingEntireFleet(false);
            }
        };
        fetchData();
    }, [apiURL, dbName, user]);


    // Filter-dependent data fetches
    useEffect(() => {
        setConvertedDataLoading(true);
        setFetchedVehicleData(null);
        setConvertedEmissionData(null);
        setConvertedGraphData(null);
        setConvertedSummaryData(null);
        if (selectedGroups && beginDate && endDate) {
            const fetchData = async () => {
                setIsLoadingVehicleData(true);
                setVehicleDataError(null);
                try {
                    let groupList = selectedGroups.map((g) => g.id);
                    const data = await fetchVehicleData(user)(`${apiURL}emitDataForFleet?dbName=${dbName}&group=${groupList}&start=${formatDateParamsForApi(beginDate)}&stop=${formatDateParamsForApi(endDate)}`);
                    setFetchedVehicleData(data);
                } catch (err) {
                    setVehicleDataError(err);
                } finally {
                    setIsLoadingVehicleData(false);
                }
            };
            fetchData();
        }
    }, [apiURL, dbName, user, selectedGroups, beginDate, endDate]);

    useEffect(() => {
        setFetchedIdleData(null);
        if (beginDate && endDate) {
            const fetchData = async () => {
                setIsLoadingIdleData(true);
                setIdleDataError(null);
                try {
                    const data = await sawatchApiFetchCall(user)(`${apiURL}idleDataForFleet?dbName=${dbName}&start=${formatDateParamsForApi(beginDate)}&stop=${formatDateParamsForApi(endDate)}`);
                    setFetchedIdleData(data);
                } catch (err) {
                    setIdleDataError(err);
                } finally {
                    setIsLoadingIdleData(false);
                }
            };
            fetchData();
        }
    }, [apiURL, dbName, user, beginDate, endDate]);

    useEffect(() => {
        setConvertedDataLoading(true);
        setFetchedGraphData(null);
        setConvertedEmissionData(null);
        setConvertedGraphData(null);
        setConvertedSummaryData(null);
        if (selectedGroups && selectedVehicleClasses.length > 0 && beginDate && endDate) {
            const fetchData = async () => {
                setIsLoadingGraphData(true);
                setGraphDataError(null);
                try {
                    let groupList = selectedGroups.map((g) => g.id);
                    const data = await fetchGraphData(user)(`${apiURL}emitGraphData?dbName=${dbName}&group=${groupList}&start=${formatDateParamsForApi(beginDate)}&stop=${formatDateParamsForApi(endDate)}&selectedVehicleClasses=${selectedVehicleClasses}`);
                    setFetchedGraphData(data);
                } catch (err) {
                    setGraphDataError(err);
                } finally {
                    setIsLoadingGraphData(false);
                }
            };
            fetchData();
        }
    }, [apiURL, dbName, user, selectedGroups, selectedVehicleClasses, beginDate, endDate]);


    // --- Primary Data Initialization (runs once on first fetch) ---
    useEffect(() => {
        if (fetchedBoundsData && !dateBoundsError) {
            const beginDate = fetchedBoundsData.beginDate;
            const endDate = fetchedBoundsData.endDate;
            setBeginDate(beginDate);
            setEndDate(endDate);
            setMinDate(beginDate);
            setMaxDate(endDate);
        }
    }, [fetchedBoundsData, dateBoundsError, setBeginDate, setEndDate, setMinDate, setMaxDate]);

    useEffect(() => {
        if (fetchedVehicleClassesData && !vehicleClassesError) {
            setVehicleClasses(fetchedVehicleClassesData);
            setSelectedVehicleClasses(fetchedVehicleClassesData);
        }
    }, [fetchedVehicleClassesData, vehicleClassesError, setVehicleClasses, setSelectedVehicleClasses]);

    useEffect(() => {
        if (fetchedGroupsData && !groupsError) {
            setGroups(fetchedGroupsData);
            if (fetchedGroupsData.length > 0) {
                 setSelectedGroups([{id: fetchedGroupsData[0].id, name: fetchedGroupsData[0].name}]);
            }
        }
    }, [fetchedGroupsData, groupsError, setGroups, setSelectedGroups]);

    useEffect(() => {
        if (fetchedDbSettingsData && !dbSettingsError) {
            setDbSettings(fetchedDbSettingsData);
            setMethodology(deriveMethodObjFromSettings(fetchedDbSettingsData.emissions_methodology));
        }
    }, [fetchedDbSettingsData, dbSettingsError, setDbSettings, setMethodology]);

    useEffect(() => {
        if (fetchedEntireFleetVehicleData && !entireFleetDataError) {
            setFleetHasAltFuels(hasAltFuelVehicles(fetchedEntireFleetVehicleData));
        }
    }, [fetchedEntireFleetVehicleData, entireFleetDataError, setFleetHasAltFuels]);


    // --- Coordinated Data Processing Flow ---
    const processAndSetEmissionData = useCallback(async () => {
        setRawEmissionData(null);
        if (fetchedVehicleData && fetchedIdleData && methodology && !vehicleDataError && !idleDataError) {
            setConvertedDataLoading(true); // Trigger loading indicator here
            let preprocessedVehicleList = preprocessVehicleList(fetchedVehicleData, fetchedIdleData, methodology, dbSettings);
            let emissionsData = await calculateEmissionsFromVehicleData(preprocessedVehicleList, calcByFuel, user, secrets.emitApiURL);
            setRawEmissionData(emissionsData);
        }
    }, [fetchedVehicleData, fetchedIdleData, methodology, dbSettings, calcByFuel, user, secrets, vehicleDataError, idleDataError]);

    useEffect(() => {
        processAndSetEmissionData();
    }, [processAndSetEmissionData]);


    useEffect(() => {
        setClassFilteredEmissionData(null);
        if (rawEmissionData && selectedVehicleClasses.length > 0) {
            let classFilteredData = filterForSelectedClasses(selectedVehicleClasses, rawEmissionData);
            setClassFilteredEmissionData(classFilteredData);
        }
    }, [rawEmissionData, selectedVehicleClasses]);

    useEffect(() => {
        if (classFilteredEmissionData) {
            let summaryData = summarizeEmissions(classFilteredEmissionData);
            setRawSummaryData(summaryData);
        }
    }, [classFilteredEmissionData]);

    const processAndSetGraphData = useCallback(() => {
        if (fetchedGraphData && rawSummaryData && !graphDataError) {
            setConvertedDataLoading(true); // Trigger loading indicator here
            let processedGraphData = processGraphData(fetchedGraphData, rawSummaryData, beginDate, endDate, dbSettings, user.userSettings);
            setRawGraphData(processedGraphData);
        }
    }, [fetchedGraphData, rawSummaryData, beginDate, endDate, dbSettings, graphDataError, user.userSettings]);

    useEffect(() => {
        processAndSetGraphData();
    }, [processAndSetGraphData]); // Now depends on the memoized callback


    // Convert data to macroScale & user settings whenever raw data gets updated
    useEffect(() => {
        if (classFilteredEmissionData) {
            setConvertedEmissionData(convertEmissionData(classFilteredEmissionData, metricUnitPrefs, macroScale, user.userSettings));
        }
    }, [classFilteredEmissionData, macroScale, user, metricUnitPrefs]);

    useEffect(() => {
        if (rawSummaryData) {
            setConvertedSummaryData(convertSummaryData(rawSummaryData, metricUnitPrefs, macroScale, user.userSettings));
        }
    }, [rawSummaryData, macroScale, user, metricUnitPrefs]);

    useEffect(() => {
        if (rawGraphData) {
            setConvertedGraphData(convertGraphData(rawGraphData, metricUnitPrefs, macroScale, user.userSettings));
            // This is the last data conversion, and we can be confident that it will finish after everything else, as it depends on the summary data which depends on the emission data, so we can finally set loading to false
        }
    }, [rawGraphData, macroScale, user, metricUnitPrefs]);

    useEffect(() => {
        if(fetchedGraphData && fetchedVehicleData && classFilteredEmissionData && convertedGraphData && convertedSummaryData && convertedEmissionData) {
            setConvertedDataLoading(false);
        }
    }, [fetchedGraphData, fetchedVehicleData, classFilteredEmissionData, convertedGraphData, convertedSummaryData, convertedEmissionData]);

    // UPDATE PDFS
    const [instance, updateInstance] = usePDF({ document: <Document /> });
    useEffect(() => {
        if (!convertedDataLoading && convertedSummaryData && convertedGraphData) {
            updateInstance(
                <PdfReport
                    summaryData={convertedSummaryData}
                    graphData={convertedGraphData}
                    selectedVehicleClasses={selectedVehicleClasses}
                    vehicleClasses={vehicleClasses}
                    selectedGroups={selectedGroups}
                    viewDataByMonth={showMonths}
                    methodology={methodology}
                    isTd={isTd}
                    fleetHasAltFuels={fleetHasAltFuels}
                    dbDisplayName={dbDisplayName}
                    userSettings={user.userSettings}
                    metricUnitPrefs={metricUnitPrefs}
                    macroScale={macroScale}
                    endDate={endDate}
                    beginDate={beginDate}
                />
            );
        }
    }, [selectedVehicleClasses, vehicleClasses, selectedGroups, showMonths, methodology, macroScale, convertedSummaryData, convertedGraphData, endDate, beginDate, convertedDataLoading, updateInstance, isTd, fleetHasAltFuels, dbDisplayName, user, metricUnitPrefs]);

    // TODO: Implement actual error handling, like we have in ezEV/ezIO. -NK
    // The below variables are just one option we could use to manage this. (Future developers should remove the unused variables comment block if you use these, obviously.)
    /* eslint-disable no-unused-vars */
    const reqParamsLoading = isLoadingBounds || isLoadingDbSettings || isLoadingGroups || isLoadingVehicleClasses;
    const rawDataLoading = isLoadingEntireFleet || isLoadingIdleData || isLoadingVehicleData || isLoadingGraphData;
    const fetchError = dateBoundsError || vehicleClassesError || groupsError || dbSettingsError || entireFleetDataError || vehicleDataError || idleDataError || graphDataError;
    /* eslint-enable no-unused-vars */

    return (
        <ProductWrapper>
            <Router basename={basename}>
                <ProductHeader fleetDisplayName={dbDisplayName} />
                <NavBar
                    dataLoading={convertedDataLoading}
                    maxDate={maxDate}
                    minDate={minDate}
                    beginDate={beginDate}
                    handleBeginDateChange={setBeginDate}
                    endDate={endDate}
                    handleEndDateChange={setEndDate}
                    vehicleClasses={vehicleClasses}
                    selectedVehicleClasses={selectedVehicleClasses}
                    handleClassChange={setSelectedVehicleClasses}
                    groups={groups}
                    selectedGroups={selectedGroups}
                    handleGroupChange={setSelectedGroups}
                    macroScale={macroScale}
                    handleScaleChange={setMacroScale}
                    downloadUrl={instance.url}
                    dbDisplayName={dbDisplayName}
                    methodology={methodology}
                    handleMethodChange={setMethodology}
                    calcByFuel={calcByFuel}
                    handleModelChange={setCalcByFuel}
                    showEmissionsByDrivetrain={showEmissionsByDrivetrain}
                    handleDrivetrainViewChange={setShowEmissionsByDrivetrain}
                    user={user}
                />
                <ContentContainer>
                    {convertedDataLoading || reqParamsLoading || rawDataLoading ? <LoadingIndeterminate /> : // Added all loading states here
                        <Switch>
                            <Route exact path="/emit">
                                <Redirect to={`/emit/home`} />
                            </Route>
                            <Route exact path="/emit/home">
                                {pageHasData('home', rawSummaryData) ?
                                    <HomeView
                                        summaryData={convertedSummaryData}
                                        graphData={convertedGraphData}
                                        defaultToMonthView={showMonths}
                                        fleetHasAltFuels={fleetHasAltFuels}
                                        isTd={isTd}
                                        metricUnitPrefs={metricUnitPrefs}
                                        macroScale={macroScale}
                                        userSettings={user.userSettings}
                                    />
                                    :
                                    <S.NoDataMessage>{noDataMessageText('home')}</S.NoDataMessage>
                                }
                            </Route>
                            <Route exact path='/emit/generated-emissions'>
                                {pageHasData('generated-emissions', rawSummaryData) ?
                                    <GeneratedEmissionsView
                                        summaryData={convertedSummaryData}
                                        beginDate={beginDate}
                                        endDate={endDate}
                                        tableData={convertedEmissionData}
                                        graphData={convertedGraphData}
                                        selectedGroups={selectedGroups}
                                        selectedVehicleClasses={selectedVehicleClasses}
                                        vehicleClasses={vehicleClasses}
                                        dbDisplayName={dbDisplayName}
                                        defaultToMonthView={showMonths}
                                        methodology={methodology}
                                        isTd={isTd}
                                        metricUnitPrefs={metricUnitPrefs}
                                        macroScale={macroScale}
                                        userSettings={user.userSettings}
                                    />
                                    :
                                    <S.NoDataMessage>{noDataMessageText('generated-emissions')}</S.NoDataMessage>
                                }
                            </Route>
                            <Route exact path='/emit/avoided-emissions'>
                                {pageHasData('avoided-emissions', rawSummaryData) ?
                                    <AvoidedEmissionsView
                                        summaryData={convertedSummaryData}
                                        beginDate={beginDate}
                                        endDate={endDate}
                                        tableData={convertedEmissionData}
                                        graphData={convertedGraphData}
                                        selectedGroups={selectedGroups}
                                        selectedVehicleClasses={selectedVehicleClasses}
                                        vehicleClasses={vehicleClasses}
                                        dbDisplayName={dbDisplayName}
                                        defaultToMonthView={showMonths}
                                        methodology={methodology}
                                        isTd={isTd}
                                        metricUnitPrefs={metricUnitPrefs}
                                        macroScale={macroScale}
                                        userSettings={user.userSettings}
                                    />
                                    :
                                    <S.NoDataMessage>{noDataMessageText('avoided-emissions', null, fleetHasAltFuels)}</S.NoDataMessage>
                                }
                            </Route>
                            <Route exact path="/emit/about">
                                <AboutView methodology={methodology} isTd={isTd} userSettings={user.userSettings} />
                            </Route>
                        </Switch>}
                </ContentContainer>
            </Router>
        </ProductWrapper>
    );
}